import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Box,
  Typography,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ZoomIn,
  ZoomOut,
  RotateLeft,
  RotateRight,
  RestartAlt,
} from "@mui/icons-material";
import { Area } from "react-easy-crop/types";

interface ImageCropDialogProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
  onCropComplete: (croppedImage: string) => void;
  aspectRatio?: number;
  title?: string;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  imageUrl,
  onClose,
  onCropComplete,
  aspectRatio = 16 / 9,
  title = "Crop Image",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropChange = (location: { x: number; y: number }) => {
    setCrop(location);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
  };

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  const onCropCompleteCallback = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createCroppedImage = async () => {
    if (!croppedAreaPixels) return;

    try {
      const image = new Image();
      image.src = imageUrl;
      await new Promise((resolve) => {
        image.onload = resolve;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) return;

      // Handle rotation
      const rotRad = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(rotRad));
      const cos = Math.abs(Math.cos(rotRad));

      if (rotation % 180 !== 0) {
        canvas.width = croppedAreaPixels.height;
        canvas.height = croppedAreaPixels.width;
      } else {
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;
      }

      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rotRad);
      ctx.translate(-croppedAreaPixels.width / 2, -croppedAreaPixels.height / 2);

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      ctx.restore();

      canvas.toBlob((blob) => {
        if (!blob) return;
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const croppedImage = reader.result as string;
          onCropComplete(croppedImage);
        };
      }, "image/jpeg", 0.95);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleSave = () => {
    createCroppedImage();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={handleReset} size="small" title="Reset">
            <RestartAlt />
          </IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary">
          💡 Drag the image to reposition, use controls below to adjust
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          position: "relative", 
          width: "100%", 
          height: { xs: 300, sm: 400, md: 500 },
          mb: 3,
          bgcolor: "grey.100",
          borderRadius: 1,
        }}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            onRotationChange={setRotation}
          />
        </Box>

        {/* Zoom Control */}
        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Zoom ({zoom.toFixed(1)}x)
            <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 1}>
              <ZoomOut fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 3}>
              <ZoomIn fontSize="small" />
            </IconButton>
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, zoom) => setZoom(zoom as number)}
            aria-label="Zoom"
          />
        </Box>

        {/* Rotation Control */}
        <Box>
          <Typography gutterBottom>
            Rotation ({rotation}°)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={handleRotateLeft} color="primary">
              <RotateLeft />
            </IconButton>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(e, value) => setRotation(value as number)}
              aria-label="Rotation"
              sx={{ flex: 1 }}
            />
            <IconButton onClick={handleRotateRight} color="primary">
              <RotateRight />
            </IconButton>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Crop & Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;

